<template>
  <div>
    <Header :title="breadcrumb"></Header>
    <!--    <router-view class="px-9 py-9"/>-->
    <v-row>
      <v-col cols="12" sm="12" md="2" lg="2" xl="3">
        <v-card class="mx-auto" tile>
          <v-list dense>
            <v-list-item-group color="primary">
              <template v-for="item in navItems">
                <v-list-item
                  :key="item.title"
                  v-if="$canAny([item.permission])"
                  :to="item.route"
                  active-class="link-active"
                  exact
                  @click="changeBreadcrumb(item.title)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.title }}
                      <v-chip
                        v-if="item?.total > 0"
                        color="green"
                        x-small
                        class="ml-2 white--text"
                        >{{ item?.total }}</v-chip
                      >
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-list-item-group>
          </v-list>
        </v-card>
      </v-col>
      <v-col>
        <router-view />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Header from "@/components/Header";
import apolloClient from "@/apollo/apollo";
import gql from "graphql-tag";
import { title } from "process";

export default {
  props: ["id"],
  components: { Header },
  data() {
    return {
      patient: null,
      breadcrumb: "",
      patientName: "",
      navItems: [
        {
          title: "Patient Details",
          route: `/patients/${this.$route.params.id}`,
          permission: "patient_details",
        },
        {
          title: "Appointments",
          route: `/patients/${this.$route.params.id}/appointments`,
          permission: "patient_appointment",
        },
        {
          title: "Forms",
          route: `/patients/${this.$route.params.id}/form-signatures`,
          permission: "patient_form",
        },
        {
          title: "Department Note",
          route: `/patients/${this.$route.params.id}/departments`,
          permission: "patient_department_note",
        },
        {
          title: "Treatment Note",
          route: `/patients/${this.$route.params.id}/treatment-notes`,
          permission: "patient_treatment_note",
        },
        {
          title: "Public Treatment Note",
          route: `/patients/${this.$route.params.id}/public-treatment-notes`,
          permission: "authenticated",
        },
        {
          title: "Packages",
          route: `/patients/${this.$route.params.id}/packages`,
          permission: "*",
        },
        {
          title: "Files",
          route: `/patients/${this.$route.params.id}/files`,
          permission: "patient_files",
        },
        {
          title: "Child Progress & Media",
          route: `/patients/${this.$route.params.id}/media`,
          permission: "patient_media",
        },
        {
          title: "Archive Treatment Note",
          route: `/patients/${this.$route.params.id}/archived-treatment-notes`,
          permission: "patient_archive_treatment_note",
        },
        {
          title: "Reports",
          route: `/patients/${this.$route.params.id}/reports`,
          permission: "authenticated",
        },
      ],
    };
  },
  methods: {
    changeBreadcrumb(title) {
      // No longer used - kept for compatibility
      this.updateHeaderTitle();
    },
    computeSectionFromRoute(route) {
      const p = route.path;
      const id = route.params.id;

      // Exact match for patient details root
      if (p === `/patients/${id}` || p === `/patients/${id}/`) {
        return "Patient Details";
      }

      // Check for specific sections
      if (p.includes("/appointments")) return "Appointments";
      if (p.includes("/form-signatures")) return "Forms";
      if (p.includes("/departments")) return "Department Notes";
      if (p.includes("/public-treatment-notes")) return "Public Treatment Notes";
      if (p.includes("/treatment-notes")) return "Treatment Notes";
      if (p.includes("/packages")) return "Packages";
      if (p.includes("/files/private")) return "Private Files";
      if (p.includes("/files")) return "Files";
      if (p.includes("/media")) return "Media";
      if (p.includes("/archived-treatment-notes")) return "Archive Treatment Note";
      if (p.includes("/reports")) return "Reports";

      // Fallback to route meta or default
      return route.meta?.breadcrumb || "Patient Details";
    },
    updateHeaderTitle() {
      const section = this.computeSectionFromRoute(this.$route);
      const name = this.patientName?.trim();
      document.title = name;
      this.breadcrumb = name ? `${name} — ${section}` : section;
    },
  },

  async mounted() {
    // Initialize header title before data loads
    this.updateHeaderTitle();

    try {
      const response = await apolloClient.query({
        query: gql`
          query findOnePatientById($id: Int!) {
            findOnePatientById(id: $id) {
              submenuStatics {
                files
                appointments
                forms
                treatmentNotes
                media
                publicTreatmentNotes
              }
              id
              listOfFormSignId
              nationalId
              appointmentNotes
              contact {
                country {
                  id
                }
                firstName
                lastName
                state
                address1
                address2
                address3
                city
                companyName
                contactPhones {
                  id
                  phone
                  type
                }
                email
                id
                note
                occupation
                postCode
                preferredName
                timeZone
                type
              }
              extraInfo
              sex
              birthDate
              bookingEmailConfirm
              cancelEmailConfirm
              acceptPolicy
              emergencyContact
              referenceNumber
              medicareNumber
            }
          }
        `,
        variables: { id: +this.$route.params.id },
        context: {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        },
      });

      this.patient = response.data.findOnePatientById;

      // Update submenu item counts
      this.navItems.forEach((item) => {
        if (item.title == "Appointments") {
          item.total =
            response.data.findOnePatientById.submenuStatics.appointments;
        } else if (item.title == "Forms") {
          item.total = response.data.findOnePatientById.submenuStatics.forms;
        } else if (item.title == "Files") {
          item.total = response.data.findOnePatientById.submenuStatics.files;
        } else if (item.title == "Treatment Note") {
          item.total =
            response.data.findOnePatientById.submenuStatics.treatmentNotes;
        } else if (item.title == "Media") {
          item.total = response.data.findOnePatientById.submenuStatics.media;
        } else if (item.title == "Public Treatment Note") {
          item.total =
            response.data.findOnePatientById.submenuStatics.publicTreatmentNotes;
        }
      });

      // Set patient name from first and last name
      this.patientName =
        response.data.findOnePatientById.contact.firstName +
        " " +
        response.data.findOnePatientById.contact.lastName;

      // Update header title with patient name
      this.updateHeaderTitle();
    } catch (error) {
      console.error("Failed to fetch:", error.message);
    }
  },

  watch: {
    $route() {
      // Update header title whenever route changes within patient details
      this.updateHeaderTitle();
    },
  },
};
</script>

<style scoped></style>
